/**
 * Centralized formatting utilities.
 *
 * All functions are PURE — no sessionStorage reads.
 * Currency / locale are always passed as arguments.
 *
 * Usage:
 *   import { formatPrice, formatCurrency, formatDate, formatDateRange } from '../utils/format';
 */
import type { Money } from '../../types';

// ─── Price / Currency ───────────────────────────────────────────────

/**
 * Format a Commercetools Money-like value for display.
 *
 * @param price  - Either a Money object or an object with a `.value` Money property.
 * @param locale - BCP-47 locale string (defaults to "en").
 * @returns Formatted string, e.g. "$12.34"
 */
export function formatPrice(
  price: Money | { value: Money },
  locale: string = 'en'
): string {
  const value: Money = 'value' in price && price.value ? price.value : (price as Money);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: value.currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value.centAmount / 100);
}

/**
 * Format a raw centAmount + currencyCode as a currency string.
 */
export function formatCurrency(
  centAmount: number,
  currencyCode: string,
  locale: string = 'en'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(centAmount / 100);
}

// ─── Discount ───────────────────────────────────────────────────────

interface DiscountValue {
  type: 'relative' | 'absolute';
  permyriad?: number;
  money?: Money[];
}

/**
 * Format a discount for display (percentage or absolute).
 *
 * @param discount      - Object with a `.value` describing the discount.
 * @param currencyCode  - Active currency code (e.g. "USD").
 * @param locale        - BCP-47 locale string.
 */
export function formatDiscount(
  discount: { value: DiscountValue },
  currencyCode: string,
  locale: string = 'en'
): string {
  if (discount.value.type === 'relative' && discount.value.permyriad != null) {
    return `${discount.value.permyriad / 100}%`;
  }
  if (discount.value.type === 'absolute' && discount.value.money) {
    const money = discount.value.money.find((m) => m.currencyCode === currencyCode);
    if (money) {
      return formatPrice(money, locale);
    }
  }
  return 'unknown discount type';
}

/**
 * Calculate a discount as a percentage of a subtotal.
 */
export function calculateDiscountPercentage(
  discountAmount: number,
  subtotal: number
): number {
  if (!subtotal || subtotal === 0) return 0;
  return Math.round((discountAmount / subtotal) * 100);
}

// ─── Date ───────────────────────────────────────────────────────────

/**
 * Format a single date for display.
 */
export function formatDate(date: Date | string, locale: string = 'en'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = d.getDate();
  const month = d.toLocaleDateString(locale, { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
}

/**
 * Format a date range — handles both single-day and multi-day ranges.
 */
export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string {
  if (!startDate || !endDate) return 'Dates TBA';

  if (startDate === endDate) {
    return formatDate(startDate);
  }

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}
